/**
 * Admin – Server-Status/Auslastung (Chris-Feedback 2026-07-30)
 *
 * Reine Anzeige, kein Schreibzugriff. DSGVO-Grenze: Backend liefert ausschließlich
 * Aggregatzahlen (CPU/RAM/Disk, DB/Redis-Latenz, PM2-Prozessdaten, Anzahl aktiver Sessions) —
 * keine Spieler-IDs/Usernames.
 *
 * Route (Backend): GET /api/v1/admin/system-status
 */

import { api, ApiError } from '../../services/api.js';

interface ResourceUsage {
  cpuCount: number;
  loadAvg1m: number;
  loadAvg5m: number;
  loadAvg15m: number;
  memTotalBytes: number;
  memFreeBytes: number;
  memUsedPercent: number;
}

interface DiskUsage {
  totalBytes: number;
  freeBytes: number;
  usedPercent: number;
}

interface DbRedisHealth {
  ok: boolean;
  latencyMs: number | null;
}

interface Pm2ProcessInfo {
  name: string;
  pid: number | null;
  status: string;
  uptimeMs: number | null;
  memoryBytes: number | null;
  cpuPercent: number | null;
  restarts: number;
}

interface Pm2Status {
  available: boolean;
  processes: Pm2ProcessInfo[];
}

interface SystemStatus {
  timestamp: string;
  resources: ResourceUsage;
  disk: DiskUsage | null;
  db: DbRedisHealth;
  redis: DbRedisHealth;
  pm2: Pm2Status;
  activeSessions: number;
  onlinePlayers: number;
  onlineThresholdMinutes: number;
}

let containerId = '';

export function initAdminSystemStatus(targetContainerId: string): void {
  containerId = targetContainerId;
  const el = document.getElementById(containerId);
  if (!el) return;

  el.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
      <p id="asys-updated" style="color:#9090a8;font-size:12px;margin:0;">LADE...</p>
      <button id="asys-refresh" style="border:1px solid #457b9d;color:#457b9d;background:transparent;
              padding:4px 12px;cursor:pointer;font-family:'Share Tech Mono',monospace;font-size:12px;">
        AKTUALISIEREN
      </button>
    </div>
    <div id="asys-content"><p style="color:#9090a8;">LADE SERVER-STATUS...</p></div>
  `;

  document.getElementById('asys-refresh')?.addEventListener('click', () => { void loadStatus(); });
  void loadStatus();
}

async function loadStatus(): Promise<void> {
  const contentEl = document.getElementById('asys-content');
  const updatedEl = document.getElementById('asys-updated');
  if (!contentEl) return;

  try {
    // api.get() entpackt die {data, meta}-Hülle bereits automatisch (fetchApi::hasDataEnvelope).
    const status = await api.get<SystemStatus>('/admin/system-status');
    if (updatedEl) updatedEl.textContent = `STAND: ${new Date(status.timestamp).toLocaleTimeString('de-DE')}`;
    contentEl.innerHTML = renderStatus(status);
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      contentEl.innerHTML = `<p style="color:#e63946;">KEIN ADMIN-ZUGRIFF.</p>`;
      return;
    }
    contentEl.innerHTML = `<p style="color:#e63946;">Fehler beim Laden des Server-Status.</p>`;
  }
}

function fmtBytes(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(0)} MB`;
}

function fmtUptime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${String(days)}d ${String(hours)}h`;
  if (hours > 0) return `${String(hours)}h ${String(minutes)}m`;
  return `${String(minutes)}m`;
}

function usageColor(percent: number): string {
  if (percent >= 90) return '#e63946';
  if (percent >= 70) return '#f0b429';
  return '#52b788';
}

function renderBar(label: string, percent: number, sublabel: string): string {
  const color = usageColor(percent);
  return `
    <div style="margin-bottom:10px;">
      <div style="display:flex;justify-content:space-between;font-size:12px;color:#9090a8;margin-bottom:3px;">
        <span>${label}</span>
        <span style="color:${color};">${sublabel}</span>
      </div>
      <div style="height:8px;background:#1c1c26;border:1px solid #2a2a38;">
        <div style="height:100%;width:${String(Math.min(100, Math.max(0, percent)))}%;background:${color};"></div>
      </div>
    </div>
  `;
}

function renderHealthPill(label: string, health: DbRedisHealth): string {
  const color = health.ok ? '#52b788' : '#e63946';
  const text = health.ok ? `OK (${String(health.latencyMs)}ms)` : 'NICHT ERREICHBAR';
  return `
    <div style="display:flex;justify-content:space-between;padding:6px 10px;border:1px solid #2a2a38;margin-bottom:6px;">
      <span style="color:#9090a8;font-size:12px;">${label}</span>
      <span style="color:${color};font-size:12px;font-weight:bold;">${text}</span>
    </div>
  `;
}

function renderPm2Table(pm2: Pm2Status): string {
  if (!pm2.available) {
    return `<p style="color:#9090a8;font-size:12px;">PM2 nicht verfügbar (kein PM2-Daemon auf diesem Host — normal in der lokalen Entwicklung).</p>`;
  }
  if (pm2.processes.length === 0) {
    return `<p style="color:#9090a8;font-size:12px;">PM2 läuft, aber keine Prozesse gefunden.</p>`;
  }
  return `
    <table style="width:100%;border-collapse:collapse;font-family:'Share Tech Mono',monospace;font-size:12px;">
      <thead>
        <tr style="color:#f0b429;text-align:left;border-bottom:1px solid #2a2a38;">
          <th style="padding:6px 8px;">PROZESS</th>
          <th style="padding:6px 8px;">STATUS</th>
          <th style="padding:6px 8px;">UPTIME</th>
          <th style="padding:6px 8px;">RAM</th>
          <th style="padding:6px 8px;">CPU</th>
          <th style="padding:6px 8px;">RESTARTS</th>
        </tr>
      </thead>
      <tbody>
        ${pm2.processes.map((p) => {
          const statusColor = p.status === 'online' ? '#52b788' : p.status === 'stopped' ? '#9090a8' : '#e63946';
          return `
            <tr style="border-bottom:1px solid #1a1a22;">
              <td style="padding:6px 8px;color:#e8e8f0;">${escapeHtml(p.name)}</td>
              <td style="padding:6px 8px;color:${statusColor};">${escapeHtml(p.status)}</td>
              <td style="padding:6px 8px;color:#9090a8;">${p.uptimeMs !== null ? fmtUptime(p.uptimeMs) : '–'}</td>
              <td style="padding:6px 8px;color:#9090a8;">${p.memoryBytes !== null ? fmtBytes(p.memoryBytes) : '–'}</td>
              <td style="padding:6px 8px;color:#9090a8;">${p.cpuPercent !== null ? `${String(p.cpuPercent)}%` : '–'}</td>
              <td style="padding:6px 8px;color:#9090a8;">${String(p.restarts)}</td>
            </tr>
          `;
        }).join('')}
      </tbody>
    </table>
  `;
}

function renderStatus(status: SystemStatus): string {
  const { resources, disk, db, redis, pm2, activeSessions, onlinePlayers, onlineThresholdMinutes } = status;

  return `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:20px;">
      <section>
        <h3 style="color:#f0b429;font-size:13px;letter-spacing:0.06em;margin:0 0 10px;">RESSOURCEN</h3>
        ${renderBar('RAM', resources.memUsedPercent, `${resources.memUsedPercent.toFixed(1)}% (${fmtBytes(resources.memTotalBytes - resources.memFreeBytes)} / ${fmtBytes(resources.memTotalBytes)})`)}
        ${disk ? renderBar('DISK (/)', disk.usedPercent, `${disk.usedPercent.toFixed(1)}% (${fmtBytes(disk.totalBytes - disk.freeBytes)} / ${fmtBytes(disk.totalBytes)})`) : `<p style="color:#9090a8;font-size:12px;">DISK: nicht verfügbar (kein POSIX-Host)</p>`}
        <p style="color:#9090a8;font-size:12px;margin-top:8px;">
          LOAD AVG: ${resources.loadAvg1m.toFixed(2)} / ${resources.loadAvg5m.toFixed(2)} / ${resources.loadAvg15m.toFixed(2)}
          &nbsp;(${String(resources.cpuCount)} CPU-Kerne)
        </p>
      </section>

      <section>
        <h3 style="color:#f0b429;font-size:13px;letter-spacing:0.06em;margin:0 0 10px;">VERBINDUNGEN</h3>
        ${renderHealthPill('POSTGRESQL', db)}
        ${renderHealthPill('REDIS', redis)}
        <div style="display:flex;justify-content:space-between;padding:6px 10px;border:1px solid #2a2a38;margin-top:10px;">
          <span style="color:#9090a8;font-size:12px;">SITZUNGEN</span>
          <span style="color:#457b9d;font-size:12px;font-weight:bold;">${String(activeSessions)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:6px 10px;border:1px solid #2a2a38;margin-top:6px;" title="Echte Interaktion in den letzten ${String(onlineThresholdMinutes)} Minuten">
          <span style="color:#9090a8;font-size:12px;">ONLINE (${String(onlineThresholdMinutes)} MIN)</span>
          <span style="color:#52b788;font-size:12px;font-weight:bold;">${String(onlinePlayers)}</span>
        </div>
      </section>
    </div>

    <section style="margin-top:20px;">
      <h3 style="color:#f0b429;font-size:13px;letter-spacing:0.06em;margin:0 0 10px;">PM2-PROZESSE</h3>
      ${renderPm2Table(pm2)}
    </section>
  `;
}

/** XSS-Schutz: HTML-Sonderzeichen escapen */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
